import { Collection } from '../types/collection';

import { reduce } from '../collections/reduce';
import { is } from '../util/is';

/**
 * Takes a function of type `A -> string` and a [[Collection]] of type `F<A>` and returns a record of collections of type `F<A>` grouped by the string.
 *
 * ```ts
 * tt.groupBy(num => num % 2 === 0 ? 'even' : 'odd', [1, 2, 3])
 * // => { even: [2], odd: [1, 3] }
 * ```
 *
 * @param fn A function returning the string by which to group the objects.
 * @param c The collection to be grouped.
 */
export function groupBy<A>(fn: (a: A) => string, c: A[]): Record<string, A[]>;
export function groupBy<A>(fn: (a: A) => string, c: Record<string, A>): Record<string, Record<string, A>>;
export function groupBy<A>(fn: (a: A) => string, c: Collection<A>): Record<string, Collection<A>> {
  if (is(Array, c)) {
    return reduce(
      (acc, x, k) => {
        const groupKey = fn(x);
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(x);
        return acc;
      },
      {} as Record<string, A[]>,
      c
    );
  } else {
    return reduce(
      (acc, x, k) => {
        const groupKey = fn(x);
        if (!acc[groupKey]) {
          acc[groupKey] = {};
        }
        acc[groupKey][k] = x;
        return acc;
      },
      {} as Record<string, Record<string, A>>,
      c
    );
  }
}
